/**
 * useLeadFormState — state machine for the unified <LeadForm> component
 * (Unit 4 of the listing-conversion overhaul plan).
 *
 * The state machine is the brain behind <LeadForm>. It tracks which step
 * the user is on (`qualifier` → `contact` → `success`), holds the form
 * values immutably, exposes navigation helpers (`next`, `prev`, `goTo`),
 * and wraps the caller-provided `onSubmit` so the success step transition
 * only fires after the network call resolves.
 *
 * Implementation note: the core logic is a pure reducer (`leadFormReducer`,
 * exported for unit tests) — the hook is just a thin React wrapper around
 * it. This lets us cover the state transitions without React Testing
 * Library (which the project doesn't carry).
 */

import { useCallback, useMemo, useReducer, useRef } from 'react'

import type {
  LeadFormChannel,
  LeadFormFieldName,
  LeadFormStep
} from 'src/lib/listingVariants/types'

export type LeadFormValues = {
  name: string
  phone: string
  email: string
  quantity: number | null
  sqm: number | null
  /** ISO date or StorageTime enum value as string. */
  when: string | null
  additionalInfo: string
  /**
   * NOT a form field — injected by LeadForm's submit wrapper when the BotID
   * pre-flight verdict arrived in time and flagged the session as a bot.
   * Parents forward it as `customerInfo.botIdFlagged` so the server skips
   * the host/partner notification (the lead itself always proceeds).
   */
  botIdFlagged?: boolean
}

export type LeadFormError = {
  field: LeadFormFieldName | 'submit'
  message: string
}

export type LeadFormSubmitArgs = {
  channel: LeadFormChannel
  /**
   * Optional value overrides applied on top of the reducer's `state.values`
   * for this submit only. Lets a step's submit handler thread fresh form
   * values past the React batching window — `setField` enqueues a
   * `dispatch`, but `state.values` inside this `useCallback` is captured
   * from the previous render. Without overrides, the WA prefill landed
   * empty because name/phone/email had just been set.
   */
  overrides?: Partial<LeadFormValues>
  /**
   * Where to land after a successful submit.
   *   - `'success'` (default): jump straight to the success step. Used
   *     when the submit is terminal — WA hand-offs, or when no
   *     enrichment step exists.
   *   - `'next'`: advance one step. Used after the contact step submits
   *     so the user lands on the optional qualifier/enrichment step. The
   *     reducer pre-creates the deal at contact submit; the enrichment
   *     step is a follow-up that fires its own analytics event.
   */
  destination?: 'success' | 'next'
}

export type LeadFormSubmitFn = (args: LeadFormSubmitArgs) => Promise<void>

export type UseLeadFormStateArgs = {
  steps: ReadonlyArray<LeadFormStep>
  initialValues?: Partial<LeadFormValues>
  onSubmit: (values: LeadFormValues, channel: LeadFormChannel) => Promise<void>
}

export type UseLeadFormStateReturn = {
  step: LeadFormStep
  stepIndex: number
  totalSteps: number
  values: LeadFormValues
  setField: <K extends keyof LeadFormValues>(
    key: K,
    value: LeadFormValues[K]
  ) => void
  next: () => void
  prev: () => void
  goTo: (step: LeadFormStep) => void
  submit: LeadFormSubmitFn
  isSubmitting: boolean
  error: LeadFormError | null
  reset: () => void
}

export const DEFAULT_LEAD_FORM_VALUES: LeadFormValues = {
  name: '',
  phone: '',
  email: '',
  quantity: null,
  sqm: null,
  when: null,
  additionalInfo: ''
}

// --- Reducer --------------------------------------------------------------

export type LeadFormState = {
  stepIndex: number
  values: LeadFormValues
  isSubmitting: boolean
  error: LeadFormError | null
}

export type LeadFormAction =
  | { type: 'SET_FIELD'; key: keyof LeadFormValues; value: unknown }
  | { type: 'NEXT'; totalSteps: number }
  | { type: 'PREV' }
  | { type: 'GO_TO'; stepIndex: number }
  | { type: 'SUBMIT_START' }
  | { type: 'SUBMIT_SUCCESS'; nextStepIndex: number }
  | { type: 'SUBMIT_FAILURE'; error: LeadFormError }
  | { type: 'RESET'; initialValues: LeadFormValues }

/**
 * Pure reducer powering the lead form state machine. Exported so unit
 * tests can drive transitions deterministically without standing up a
 * React renderer.
 */
export function leadFormReducer(
  state: LeadFormState,
  action: LeadFormAction
): LeadFormState {
  switch (action.type) {
    case 'SET_FIELD': {
      return {
        ...state,
        values: {
          ...state.values,
          [action.key]: action.value
        } as LeadFormValues
      }
    }
    case 'NEXT': {
      if (state.stepIndex >= action.totalSteps - 1) {
        return state
      }
      return {
        ...state,
        stepIndex: state.stepIndex + 1
      }
    }
    case 'PREV': {
      if (state.stepIndex <= 0) {
        return state
      }
      return {
        ...state,
        stepIndex: state.stepIndex - 1
      }
    }
    case 'GO_TO': {
      if (action.stepIndex < 0) {
        return state
      }
      return {
        ...state,
        stepIndex: action.stepIndex
      }
    }
    case 'SUBMIT_START': {
      return {
        ...state,
        isSubmitting: true,
        error: null
      }
    }
    case 'SUBMIT_SUCCESS': {
      return {
        ...state,
        isSubmitting: false,
        error: null,
        stepIndex: action.nextStepIndex
      }
    }
    case 'SUBMIT_FAILURE': {
      return {
        ...state,
        isSubmitting: false,
        error: action.error
      }
    }
    case 'RESET': {
      return {
        stepIndex: 0,
        values: action.initialValues,
        isSubmitting: false,
        error: null
      }
    }
    default: {
      return state
    }
  }
}

export function buildInitialValues(
  overrides?: Partial<LeadFormValues>
): LeadFormValues {
  return {
    ...DEFAULT_LEAD_FORM_VALUES,
    ...(overrides ?? {})
  }
}

// --- Hook -----------------------------------------------------------------

export function useLeadFormState({
  steps,
  initialValues,
  onSubmit
}: UseLeadFormStateArgs): UseLeadFormStateReturn {
  const initialValuesRef = useRef<LeadFormValues>(
    buildInitialValues(initialValues)
  )

  const [state, dispatch] = useReducer(leadFormReducer, undefined, () => ({
    stepIndex: 0,
    values: initialValuesRef.current,
    isSubmitting: false,
    error: null
  }))

  const totalSteps = steps.length
  const stepIndex = Math.min(state.stepIndex, totalSteps - 1)
  const step = steps[stepIndex] ?? steps[0]

  const setField = useCallback(
    <K extends keyof LeadFormValues>(key: K, value: LeadFormValues[K]) => {
      dispatch({ type: 'SET_FIELD', key, value })
    },
    []
  )

  const next = useCallback(() => {
    dispatch({ type: 'NEXT', totalSteps })
  }, [totalSteps])

  const prev = useCallback(() => {
    dispatch({ type: 'PREV' })
  }, [])

  const goTo = useCallback(
    (target: LeadFormStep) => {
      const idx = steps.indexOf(target)
      if (idx === -1) {
        return
      }
      dispatch({ type: 'GO_TO', stepIndex: idx })
    },
    [steps]
  )

  const submit = useCallback<LeadFormSubmitFn>(
    async ({ channel, overrides, destination = 'success' }) => {
      dispatch({ type: 'SUBMIT_START' })
      // Merge overrides on top of `state.values` so callers can thread
      // fresh values past React's batching window without waiting a tick.
      const merged: LeadFormValues = overrides
        ? { ...state.values, ...overrides }
        : state.values
      try {
        await onSubmit(merged, channel)
        const successIdx = steps.indexOf('success')
        const targetIdx =
          destination === 'next'
            ? Math.min(state.stepIndex + 1, totalSteps - 1)
            : successIdx === -1
              ? totalSteps - 1
              : successIdx
        dispatch({
          type: 'SUBMIT_SUCCESS',
          nextStepIndex: targetIdx
        })
      } catch (error: unknown) {
        const message =
          error instanceof Error
            ? error.message
            : 'Algo salio mal. Intenta de nuevo.'
        dispatch({
          type: 'SUBMIT_FAILURE',
          error: { field: 'submit', message }
        })
      }
    },
    [onSubmit, state.values, state.stepIndex, steps, totalSteps]
  )

  const reset = useCallback(() => {
    dispatch({ type: 'RESET', initialValues: initialValuesRef.current })
  }, [])

  return useMemo<UseLeadFormStateReturn>(
    () => ({
      step,
      stepIndex,
      totalSteps,
      values: state.values,
      setField,
      next,
      prev,
      goTo,
      submit,
      isSubmitting: state.isSubmitting,
      error: state.error,
      reset
    }),
    [
      step,
      stepIndex,
      totalSteps,
      state.values,
      state.isSubmitting,
      state.error,
      setField,
      next,
      prev,
      goTo,
      submit,
      reset
    ]
  )
}
